import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Brain, Save, ArrowLeft, LayoutDashboard, Sparkles, Target,
  MessageSquare, Users, Package, Zap, Quote, History, Trash2,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

type Profile = {
  mission_statement?: string;
  company_name?: string;
  sector?: string;
  target_audience?: string;
  main_offer?: string;
  secondary_offers?: string;
  unique_value_prop?: string;
  tone?: string;
  language_style?: string;
  avoid_words?: string;
  signature?: string;
};

const TONES = ["professionnel", "direct", "chaleureux", "expert", "décontracté"];
const STYLES = ["vouvoiement", "tutoiement"];

export default function BusinessProfilePage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile>({ tone: "professionnel", language_style: "vouvoiement" });
  const [saving, setSaving] = useState(false);
  const [memoryCount, setMemoryCount] = useState(0);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [profileRes, memoryRes] = await Promise.all([
          fetch(`${API_URL}/api/profile/business`, { credentials: "include" }),
          fetch(`${API_URL}/api/memory/recent?limit=100`, { credentials: "include" }),
        ]);
        if (profileRes.ok) {
          const data = await profileRes.json();
          if (data.profile) setProfile((p) => ({ ...p, ...data.profile }));
        }
        if (memoryRes.ok) {
          const m = await memoryRes.json();
          setMemoryCount(m.count || 0);
        }
      } catch {/* empty */}
    })();
  }, [user]);

  const clearMemory = async () => {
    if (!confirm("Effacer toute la mémoire conversationnelle de Grafton ? Cette action est irréversible.")) return;
    setClearing(true);
    try {
      const res = await fetch(`${API_URL}/api/memory/clear`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Mémoire effacée (${d.deleted} messages)`);
        setMemoryCount(0);
      }
    } finally {
      setClearing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/profile/business`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(profile),
      });
      if (res.ok) {
        toast.success("Grafton a intégré votre profil. Il parlera maintenant comme VOUS.");
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } finally {
      setSaving(false);
    }
  };

  const update = (k: keyof Profile) => (v: string) => setProfile((p) => ({ ...p, [k]: v }));

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Sparkles className="h-8 w-8 animate-pulse text-primary" /></div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="business-profile-page">
      {/* Header */}
      <div className="border-b border-border/50 bg-card/30 backdrop-blur px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-bold">L'ADN de votre Grafton</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" /> Retour</Button>
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate("/dashboard")}><LayoutDashboard className="h-4 w-4" /> Dashboard</Button>
          <Button size="sm" className="gap-1.5" onClick={save} disabled={saving} data-testid="save-profile-btn">
            <Save className="h-4 w-4" /> {saving ? "Sauvegarde..." : "Enregistrer"}
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/15 via-card to-card p-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
          <div className="relative space-y-3 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-semibold text-primary uppercase tracking-wider">Intelligence personnalisée</span>
            </div>
            <h2 className="text-3xl font-bold leading-tight">
              Apprenez à Grafton qui vous êtes.<br />
              <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">Il deviendra VOTRE Grafton.</span>
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Ce profil est injecté dans <b>chaque réponse</b> de Grafton : chat, briefings matin/soir, rapports des 9 agents NanoCorp, emails de prospection. Plus vous êtes précis, plus il sonne personnel.
            </p>
          </div>
        </div>

        {/* Mission statement (le plus important) */}
        <Card className="border-primary/30 border-2">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center gap-2">
              <Quote className="h-4 w-4 text-primary" />
              <Label className="text-sm font-semibold uppercase tracking-wider text-primary">Votre mission en 1 phrase</Label>
              <span className="text-xs text-muted-foreground font-normal">(le plus important)</span>
            </div>
            <Textarea
              placeholder="Ex: Je suis consultant IA pour PME, ma cible sont les DAF, je vends un audit à 5000€ qui libère 10h/semaine."
              value={profile.mission_statement || ""}
              onChange={(e) => update("mission_statement")(e.target.value)}
              className="min-h-[90px] text-base resize-none"
              data-testid="mission-statement-input"
            />
            <p className="text-xs text-muted-foreground">
              💡 Cette phrase sera utilisée dans <b>toutes</b> les réponses de Grafton. Pensez "pitch asc.senseur".
            </p>
          </CardContent>
        </Card>

        {/* Identité entreprise */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-border/50">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                <Label className="text-sm font-semibold uppercase tracking-wider">Identité</Label>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Nom de votre entreprise</Label>
                <Input value={profile.company_name || ""} onChange={(e) => update("company_name")(e.target.value)} placeholder="LBAL INVEST" data-testid="company-name-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Secteur</Label>
                <Input value={profile.sector || ""} onChange={(e) => update("sector")(e.target.value)} placeholder="Conseil B2B, SaaS, E-commerce..." data-testid="sector-input" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/50">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                <Label className="text-sm font-semibold uppercase tracking-wider">Cible</Label>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Audience cible</Label>
                <Textarea
                  placeholder="Ex: DAF de PME 50-200 salariés, secteur industrie"
                  value={profile.target_audience || ""}
                  onChange={(e) => update("target_audience")(e.target.value)}
                  className="min-h-[80px] resize-none"
                  data-testid="target-audience-input"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Offres */}
        <Card className="border-border/50">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-primary" />
              <Label className="text-sm font-semibold uppercase tracking-wider">Vos offres</Label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Offre principale</Label>
                <Input value={profile.main_offer || ""} onChange={(e) => update("main_offer")(e.target.value)} placeholder="Ex: Audit IA complet à 5000€" data-testid="main-offer-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Autres offres (optionnel)</Label>
                <Input value={profile.secondary_offers || ""} onChange={(e) => update("secondary_offers")(e.target.value)} placeholder="Ex: Coaching mensuel 1500€, formation 2 jours" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block flex items-center gap-1.5"><Zap className="h-3 w-3 text-primary" /> Ce qui vous rend unique (USP)</Label>
              <Textarea
                placeholder="Ex: 10 ans d'expérience terrain, garantie résultats, méthodologie brevetée..."
                value={profile.unique_value_prop || ""}
                onChange={(e) => update("unique_value_prop")(e.target.value)}
                className="min-h-[70px] resize-none"
                data-testid="usp-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Style de communication */}
        <Card className="border-border/50">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-primary" />
              <Label className="text-sm font-semibold uppercase tracking-wider">Style de communication</Label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Ton</Label>
                <div className="flex flex-wrap gap-2">
                  {TONES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => update("tone")(t)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        profile.tone === t
                          ? "bg-primary/20 border-primary/60 text-primary"
                          : "bg-background/50 border-border/50 text-muted-foreground hover:border-primary/30"
                      }`}
                      data-testid={`tone-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Adressage</Label>
                <div className="flex gap-2">
                  {STYLES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => update("language_style")(s)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        profile.language_style === s
                          ? "bg-primary/20 border-primary/60 text-primary"
                          : "bg-background/50 border-border/50 text-muted-foreground hover:border-primary/30"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Mots / expressions à éviter</Label>
              <Input value={profile.avoid_words || ""} onChange={(e) => update("avoid_words")(e.target.value)} placeholder="Ex: 'synergies', 'disruption', 'leveraging'..." />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Signature email</Label>
              <Textarea
                placeholder="Ex:&#10;Marc Dupont&#10;CEO, Grafton Conseil&#10;06 12 34 56 78"
                value={profile.signature || ""}
                onChange={(e) => update("signature")(e.target.value)}
                className="min-h-[70px] resize-none font-mono text-sm"
              />
            </div>
          </CardContent>
        </Card>

        {/* Memory card */}
        <Card className="border-border/50">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-primary" />
                <Label className="text-sm font-semibold uppercase tracking-wider">Mémoire conversationnelle</Label>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary font-mono" data-testid="memory-count">
                  {memoryCount} message{memoryCount !== 1 ? "s" : ""}
                </span>
                {memoryCount > 0 && (
                  <Button variant="outline" size="sm" onClick={clearMemory} disabled={clearing} className="gap-1.5 h-8 text-xs" data-testid="clear-memory-btn">
                    <Trash2 className="h-3 w-3" /> Effacer
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Grafton se souvient automatiquement de vos <b>10 derniers échanges</b> (cross-sessions, sur 30 jours). Cette mémoire est injectée dans chaque prompt, pour qu'il assure la continuité comme un collègue.
            </p>
          </CardContent>
        </Card>

        {/* Save button (bottom) */}
        <div className="flex justify-end pt-4">
          <Button size="lg" className="gap-2 h-12 px-8" onClick={save} disabled={saving}>
            <Save className="h-4 w-4" /> {saving ? "Sauvegarde..." : "Enregistrer l'ADN"}
          </Button>
        </div>
      </div>
    </div>
  );
}
